import {
  getDesignTotalDuration,
  getPageStartTime,
  getPagePreviewEndTime
} from "lib/designPlaybackUtils";
import { getPlaybackSegmentAtTime } from "lib/pageTransitionUtils";

/**
 * EditorDesignPlaybackOps
 *
 * Manages design-wide playback operations for the Editor component.
 * All methods are designed to be bound to the Editor component instance.
 *
 * IMPORTANT: This module uses ONLY Redux state (via updateContextState) to avoid
 * race conditions between local state and Redux props. All state updates are
 * batched into a single updateContextState call per animation frame.
 */
const EditorDesignPlaybackOps = {
  /**
   * Initialize design playback refs on the Editor instance.
   * Call this in the Editor constructor.
   */
  initializeDesignPlaybackRefs() {
    this.designPlaybackFrame = null;
    this.designPlaybackStartTime = null;
    this.designPlaybackPausedTime = 0;
    this.lastPlaybackSegment = null;
    this.designPlaybackPreviewEndTime = null;
  },

  /**
   * Handle design playback toggle (three-state: stopped → play, playing → pause, paused → resume)
   */
  handleDesignPlaybackToggle() {
    const { isDesignPlaybackMode } = this.props;

    if (isDesignPlaybackMode) {
      const isPaused = this.designPlaybackPausedTime > 0;
      if (isPaused) {
        EditorDesignPlaybackOps.resumeDesignPlayback.call(this);
      } else {
        EditorDesignPlaybackOps.pauseDesignPlayback.call(this);
      }
    } else {
      EditorDesignPlaybackOps.startDesignPlayback.call(this);
    }
  },

  /**
   * Start design-wide playback from the beginning or a specified time
   * @param {number} fromTime - Optional time in ms to start from
   * @param {number|null} previewEndTime - Optional time to auto-stop (for single page preview)
   */
  startDesignPlayback(fromTime = 0, previewEndTime = null) {
    const { designData } = this.props;
    const { pages, pagesOrder } = designData;

    const totalDuration = getDesignTotalDuration(pages, pagesOrder);

    if (totalDuration <= 0) {
      return;
    }

    // Stop any per-page playback first (uses existing per-page animation system)
    Object.keys(this.props.isPlaying || {}).forEach(pageId => {
      if (this.props.isPlaying[pageId]) {
        this.stopAnimation(pageId);
      }
    });

    // Initialize design playback refs (not state - these are instance variables)
    this.designPlaybackPausedTime = fromTime;
    this.designPlaybackStartTime = null;
    this.lastPlaybackSegment = null;
    this.designPlaybackPreviewEndTime = previewEndTime;

    // Get initial segment
    const initialSegment = getPlaybackSegmentAtTime(
      pages,
      pagesOrder,
      fromTime
    );

    // Build complete initial state in a single object
    const initialPageId = initialSegment.pageId || pagesOrder[0];
    const initialTimeInPage =
      initialSegment.type === "page" ? initialSegment.timeInPage || 0 : 0;

    // Single consolidated state update for playback start
    const playbackState = {
      // Design playback state
      isDesignPlaybackMode: true,
      isDesignPlaybackPaused: false,
      designPlaybackTime: fromTime,
      currentPlaybackSegment: initialSegment,
      // Page state
      currentPageId: initialPageId,
      // Per-page animation bridge state
      isPlaying: { [initialPageId]: true },
      isPaused: { [initialPageId]: false },
      currentPlayingTime: { [initialPageId]: initialTimeInPage }
    };

    this.props.updateContextState(playbackState);

    // Clear any existing animation frame
    if (this.designPlaybackFrame) {
      cancelAnimationFrame(this.designPlaybackFrame);
      this.designPlaybackFrame = null;
    }

    // Start the animation loop
    const updateDesignPlayback = timestamp => {
      if (!this.props.isDesignPlaybackMode) {
        return;
      }

      if (!this.designPlaybackStartTime) {
        this.designPlaybackStartTime = timestamp;
      }

      const elapsed =
        timestamp -
        this.designPlaybackStartTime +
        this.designPlaybackPausedTime;
      const currentTime = Math.min(elapsed, totalDuration);

      // Get current segment
      const segment = getPlaybackSegmentAtTime(pages, pagesOrder, currentTime);

      // Build the frame update state - start with base updates
      const frameState = {
        designPlaybackTime: currentTime,
        currentPlaybackSegment: segment
      };

      // Check for segment change and merge any segment change state
      const segmentChanged = EditorDesignPlaybackOps.hasSegmentChanged.call(
        this,
        segment
      );

      if (segmentChanged) {
        // Pass false for isCurrentlyPaused since we're actively playing
        const segmentChangeState =
          EditorDesignPlaybackOps.getSegmentChangeState.call(
            this,
            segment,
            this.lastPlaybackSegment,
            false
          );
        Object.assign(frameState, segmentChangeState);
        this.lastPlaybackSegment = segment;
      }

      // Bridge to per-page animation system: update currentPlayingTime for the current page
      // Only do this when segment HASN'T changed - when segment changes, getSegmentChangeState
      // already sets the correct animation state values and we don't want to overwrite them
      if (!segmentChanged && segment.type === "page" && segment.pageId) {
        const timeInPage = segment.timeInPage || 0;
        frameState.currentPlayingTime = {
          ...this.props.currentPlayingTime,
          [segment.pageId]: timeInPage
        };
      }

      // Single consolidated state update per frame
      this.props.updateContextState(frameState);

      // Update global playback time for elements (Caption component uses this)
      if (!window.easil) {
        window.easil = {};
      }

      if (segment.type === "page" && segment.timeInPage !== undefined) {
        window.easil.playbackTime = segment.timeInPage;
      }

      // Check if playback is complete
      const effectiveEndTime =
        this.designPlaybackPreviewEndTime !== null
          ? Math.min(this.designPlaybackPreviewEndTime, totalDuration)
          : totalDuration;

      if (segment.isComplete || currentTime >= effectiveEndTime) {
        EditorDesignPlaybackOps.stopDesignPlayback.call(this);
        return;
      }

      // Continue animation
      this.designPlaybackFrame = requestAnimationFrame(updateDesignPlayback);
    };

    this.designPlaybackFrame = requestAnimationFrame(updateDesignPlayback);
  },

  /**
   * Start a single page transition preview.
   * Plays: page animations → transition → ~2 seconds of next page, then auto-stops.
   * @param {string} pageId - The page ID to preview from
   */
  startPageTransitionPreview(pageId) {
    const { designData } = this.props;
    const { pages, pagesOrder } = designData;
    const startTime = getPageStartTime(pages, pagesOrder, pageId);
    const previewEndTime = getPagePreviewEndTime(pages, pagesOrder, pageId);
    EditorDesignPlaybackOps.startDesignPlayback.call(
      this,
      startTime,
      previewEndTime
    );
  },

  /**
   * Check if the segment has changed compared to the last segment
   * @param {Object} newSegment - New segment
   * @returns {boolean} True if segment changed
   */
  hasSegmentChanged(newSegment) {
    const lastSegment = this.lastPlaybackSegment;
    if (!lastSegment) return true;

    if (lastSegment.type !== newSegment.type) return true;
    if (lastSegment.pageId !== newSegment.pageId) return true;
    if (
      newSegment.type === "transition" &&
      lastSegment.nextPageId !== newSegment.nextPageId
    )
      return true;

    return false;
  },

  /**
   * Get state updates for segment changes (page to transition or transition to page)
   * Returns a state object to be merged into the frame update, instead of
   * directly updating state. This allows batching all updates into one call.
   *
   * @param {Object} newSegment - The new segment
   * @param {Object} oldSegment - The previous segment (may be null)
   * @param {boolean} isCurrentlyPaused - Whether playback is currently paused (for scrubbing)
   * @returns {Object} State updates to merge into frame state
   */
  getSegmentChangeState(newSegment, oldSegment, isCurrentlyPaused = false) {
    const stateUpdates = {};

    // Handle audio state changes (side effect, but lightweight)
    const shouldPlayAudio = newSegment.type === "page";
    EditorDesignPlaybackOps.handleAudioDuringDesignPlayback.call(
      this,
      shouldPlayAudio
    );

    // Handle page switching and per-page animation state bridging
    if (newSegment.type === "page") {
      const newPageId = newSegment.pageId;
      const oldPageId = oldSegment?.pageId;
      const isComingFromTransition = oldSegment?.type === "transition";

      // Update animation state when:
      // 1. Entering a new page (different from current), OR
      // 2. Coming from a transition segment (need to flip from paused to playing)
      // The second condition ensures smooth animation start after transitions
      if (
        newPageId &&
        (newPageId !== this.props.currentPageId || isComingFromTransition)
      ) {
        // Use actual paused state instead of hardcoded values
        // This ensures scrubbing while paused keeps animations paused on new page
        const newIsPlaying = { [newPageId]: !isCurrentlyPaused };
        const newIsPaused = { [newPageId]: isCurrentlyPaused };
        const newCurrentPlayingTime = {
          [newPageId]: newSegment.timeInPage || 0
        };

        // Clear old page state if different
        if (oldPageId && oldPageId !== newPageId) {
          newIsPlaying[oldPageId] = false;
          newIsPaused[oldPageId] = false;
          newCurrentPlayingTime[oldPageId] = 0;
        }

        stateUpdates.currentPageId = newPageId;
        stateUpdates.isPlaying = newIsPlaying;
        stateUpdates.isPaused = newIsPaused;
        stateUpdates.currentPlayingTime = newCurrentPlayingTime;
      }
    } else if (newSegment.type === "transition") {
      // During transitions, set animation states for both pages
      const outgoingPageId = newSegment.pageId;
      const incomingPageId = newSegment.nextPageId;
      const { designData } = this.props;
      const outgoingPageDuration =
        designData?.pages?.[outgoingPageId]?.duration || 0;

      stateUpdates.isPlaying = {
        [outgoingPageId]: false,
        ...(incomingPageId && { [incomingPageId]: false })
      };
      stateUpdates.isPaused = {
        [outgoingPageId]: true,
        ...(incomingPageId && { [incomingPageId]: true })
      };
      stateUpdates.currentPlayingTime = {
        [outgoingPageId]: outgoingPageDuration,
        ...(incomingPageId && { [incomingPageId]: 0 })
      };
    }

    // Handle video elements during transitions (side effects)
    if (newSegment.type === "transition") {
      EditorDesignPlaybackOps.handleVideosForTransition.call(
        this,
        newSegment.pageId,
        "pause"
      );
      if (newSegment.nextPageId) {
        EditorDesignPlaybackOps.handleVideosForTransition.call(
          this,
          newSegment.nextPageId,
          "prepare"
        );
      }
    } else if (
      newSegment.type === "page" &&
      oldSegment?.type === "transition"
    ) {
      EditorDesignPlaybackOps.handleVideosForTransition.call(
        this,
        newSegment.pageId,
        "start"
      );
    }

    return stateUpdates;
  },

  /**
   * Handle audio muting/unmuting during design playback
   * Audio should be muted during transitions, unmuted during pages
   * @param {boolean} shouldPlay - Whether audio should play
   */
  // eslint-disable-next-line no-unused-vars
  handleAudioDuringDesignPlayback(shouldPlay) {
    // For now, we control audio by managing elementsToPlayAudio state
    // When in a transition, we effectively mute by not including audio elements
    // Note: Full audio control may require managing actual audio elements
  },

  /**
   * Handle video elements during page transitions
   * @param {string} pageId - The page ID
   * @param {string} action - 'pause', 'prepare', or 'start'
   */
  // eslint-disable-next-line no-unused-vars
  handleVideosForTransition(pageId, action) {
    // Note: Actual video control would require accessing the video DOM elements
    // This is a placeholder for the architecture. Full implementation would
    // need to use refs or a video management system.
  },

  /**
   * Pause design-wide playback
   */
  pauseDesignPlayback() {
    if (this.designPlaybackFrame) {
      cancelAnimationFrame(this.designPlaybackFrame);
      this.designPlaybackFrame = null;
    }

    this.designPlaybackPausedTime = this.props.designPlaybackTime || 0;

    const segment = this.props.currentPlaybackSegment;
    if (
      window.easil &&
      segment?.type === "page" &&
      segment.timeInPage !== undefined
    ) {
      window.easil.playbackTime = segment.timeInPage;
    }

    const currentPageId = segment?.pageId || this.props.currentPageId;
    const currentTimeInPage = segment?.timeInPage || 0;

    // Single consolidated state update for pause
    this.props.updateContextState({
      isDesignPlaybackPaused: true,
      isPlaying: { [currentPageId]: false },
      isPaused: { [currentPageId]: true },
      currentPlayingTime: { [currentPageId]: currentTimeInPage }
    });
  },

  /**
   * Resume design-wide playback from paused position
   */
  resumeDesignPlayback() {
    const { designData } = this.props;
    const { pages, pagesOrder } = designData;

    const totalDuration = getDesignTotalDuration(pages, pagesOrder);
    const resumeTime = this.designPlaybackPausedTime;

    this.designPlaybackStartTime = null;

    if (this.designPlaybackFrame) {
      cancelAnimationFrame(this.designPlaybackFrame);
      this.designPlaybackFrame = null;
    }

    const currentSegment = getPlaybackSegmentAtTime(
      pages,
      pagesOrder,
      resumeTime
    );

    const currentPageId = currentSegment?.pageId || this.props.currentPageId;
    const currentTimeInPage = currentSegment?.timeInPage || 0;

    // Single consolidated state update for resume
    this.props.updateContextState({
      isDesignPlaybackPaused: false,
      isPlaying: { [currentPageId]: true },
      isPaused: { [currentPageId]: false },
      currentPlayingTime: { [currentPageId]: currentTimeInPage }
    });

    const updateDesignPlayback = timestamp => {
      if (!this.props.isDesignPlaybackMode) {
        return;
      }

      if (!this.designPlaybackStartTime) {
        this.designPlaybackStartTime = timestamp;
      }

      const elapsed = timestamp - this.designPlaybackStartTime + resumeTime;
      const currentTime = Math.min(elapsed, totalDuration);

      const segment = getPlaybackSegmentAtTime(pages, pagesOrder, currentTime);

      // Build the frame update state - start with base updates
      const frameState = {
        designPlaybackTime: currentTime,
        currentPlaybackSegment: segment
      };

      // Check for segment change and merge any segment change state
      const segmentChanged = EditorDesignPlaybackOps.hasSegmentChanged.call(
        this,
        segment
      );

      if (segmentChanged) {
        // Pass false for isCurrentlyPaused since we're actively playing
        const segmentChangeState =
          EditorDesignPlaybackOps.getSegmentChangeState.call(
            this,
            segment,
            this.lastPlaybackSegment,
            false
          );
        Object.assign(frameState, segmentChangeState);
        this.lastPlaybackSegment = segment;
      }

      // Bridge to per-page animation system: update currentPlayingTime for the current page
      // Only do this when segment HASN'T changed - when segment changes, getSegmentChangeState
      // already sets the correct animation state values and we don't want to overwrite them
      if (!segmentChanged && segment.type === "page" && segment.pageId) {
        const timeInPage = segment.timeInPage || 0;
        frameState.currentPlayingTime = {
          ...this.props.currentPlayingTime,
          [segment.pageId]: timeInPage
        };
      }

      // Single consolidated state update per frame
      this.props.updateContextState(frameState);

      if (!window.easil) {
        window.easil = {};
      }

      if (segment.type === "page" && segment.timeInPage !== undefined) {
        window.easil.playbackTime = segment.timeInPage;
      }

      if (segment.isComplete || currentTime >= totalDuration) {
        EditorDesignPlaybackOps.stopDesignPlayback.call(this);
        return;
      }

      this.designPlaybackFrame = requestAnimationFrame(updateDesignPlayback);
    };

    this.designPlaybackFrame = requestAnimationFrame(updateDesignPlayback);
    this.designPlaybackPausedTime = 0;
  },

  /**
   * Stop design-wide playback and reset to beginning
   */
  stopDesignPlayback() {
    if (this.designPlaybackFrame) {
      cancelAnimationFrame(this.designPlaybackFrame);
      this.designPlaybackFrame = null;
    }

    this.designPlaybackStartTime = null;
    this.designPlaybackPausedTime = 0;
    this.lastPlaybackSegment = null;
    this.designPlaybackPreviewEndTime = null;

    const { designData } = this.props;
    const firstPageId = designData.pagesOrder[0];

    // Single consolidated state update for stop
    this.props.updateContextState({
      isDesignPlaybackMode: false,
      isDesignPlaybackPaused: false,
      designPlaybackTime: 0,
      currentPlaybackSegment: null,
      currentPageId: firstPageId,
      isPlaying: {},
      isPaused: {},
      currentPlayingTime: {}
    });

    if (window.easil) {
      window.easil.playbackTime = 0;
    }
  },

  /**
   * Seek to a specific time in design-wide playback
   * @param {number} newTime - Time in milliseconds to seek to
   */
  handleDesignTimelineSeek(newTime) {
    const { designData } = this.props;
    const { pages, pagesOrder } = designData;

    const totalDuration = getDesignTotalDuration(pages, pagesOrder);
    const clampedTime = Math.max(0, Math.min(totalDuration, newTime));

    const segment = getPlaybackSegmentAtTime(pages, pagesOrder, clampedTime);

    // Determine if we're currently paused BEFORE updating refs
    // We're paused if: explicitly paused, or no active animation frame
    // Note: designPlaybackPausedTime is checked BEFORE it's updated below
    const wasPlaying =
      this.props.isDesignPlaybackMode && this.designPlaybackFrame !== null;
    const isCurrentlyPaused =
      this.props.isDesignPlaybackPaused || !this.designPlaybackFrame;

    // Update refs for tracking seek position
    if (wasPlaying) {
      this.designPlaybackPausedTime = clampedTime;
      this.designPlaybackStartTime = null;
    } else {
      this.designPlaybackPausedTime = clampedTime;
    }

    // Build seek state
    const seekState = {
      designPlaybackTime: clampedTime,
      currentPlaybackSegment: segment
    };

    // Update page if changed
    if (segment.pageId && segment.pageId !== this.props.currentPageId) {
      seekState.currentPageId = segment.pageId;
    }

    // Check for segment change and merge any segment change state
    if (EditorDesignPlaybackOps.hasSegmentChanged.call(this, segment)) {
      const segmentChangeState =
        EditorDesignPlaybackOps.getSegmentChangeState.call(
          this,
          segment,
          this.lastPlaybackSegment,
          isCurrentlyPaused
        );
      Object.assign(seekState, segmentChangeState);
      this.lastPlaybackSegment = segment;
    }

    // Always bridge to per-page animation system during seek
    // This ensures animations update even when scrubbing within the same page
    if (segment.type === "page" && segment.pageId) {
      const timeInPage = segment.timeInPage || 0;
      seekState.currentPlayingTime = {
        ...this.props.currentPlayingTime,
        [segment.pageId]: timeInPage
      };
      // When scrubbing while paused, ensure the page stays in paused state
      if (isCurrentlyPaused) {
        seekState.isPaused = {
          ...this.props.isPaused,
          [segment.pageId]: true
        };
        seekState.isPlaying = {
          ...this.props.isPlaying,
          [segment.pageId]: false
        };
      }
    } else if (segment.type === "transition") {
      // During transition scrub, both pages should be paused at their boundary times
      const outgoingPageId = segment.pageId;
      const incomingPageId = segment.nextPageId;
      const outgoingPageDuration = pages?.[outgoingPageId]?.duration || 0;

      seekState.isPlaying = {
        [outgoingPageId]: false,
        ...(incomingPageId && { [incomingPageId]: false })
      };
      seekState.isPaused = {
        [outgoingPageId]: true,
        ...(incomingPageId && { [incomingPageId]: true })
      };
      seekState.currentPlayingTime = {
        [outgoingPageId]: outgoingPageDuration,
        ...(incomingPageId && { [incomingPageId]: 0 })
      };
    }

    // Single consolidated state update for seek
    this.props.updateContextState(seekState);

    if (!window.easil) {
      window.easil = {};
    }

    if (segment.type === "page" && segment.timeInPage !== undefined) {
      window.easil.playbackTime = segment.timeInPage;
    }
  }
};

export default EditorDesignPlaybackOps;
